import merge from 'lodash/merge.js';

import User from '@/models/User.js';
import axios from '@/plugins/axios.js';
import Item from '@/store/quill/Item.js';

const THEMES = {
    blue: [
        { variable: 'primary', value: '#335CE5' },
        { variable: 'primary-light', value: '#6DBFE1' },
        { variable: 'primary-dark', value: '#3B9DDD' },
    ],
    purple: [
        { variable: 'primary', value: '#7160A6' },
        { variable: 'primary-light', value: '#D0A0E3' },
        { variable: 'primary-dark', value: '#AC7EC8' },
    ],
    yellow: [
        { variable: 'primary', value: '#ECA807' },
        { variable: 'primary-light', value: '#FFD267' },
        { variable: 'primary-dark', value: '#F6C03F' },
    ],
    green: [
        { variable: 'primary', value: '#28BDB0' },
        { variable: 'primary-light', value: '#45E8DA' },
        { variable: 'primary-dark', value: '#34CEC0' },
    ],
    black: [
        { variable: 'primary', value: '#242B36' },
        { variable: 'primary-light', value: '#6E7689' },
        { variable: 'primary-dark', value: '#4A5468' },
    ],
};

const DARKTHEMES = {
    blue: [
        { variable: 'primary', value: '#335CE5' },
        { variable: 'primary-light', value: '#6DBFE1' },
        { variable: 'primary-dark', value: '#4d76ff' },
    ],
    purple: [
        { variable: 'primary', value: '#7160A6' },
        { variable: 'primary-light', value: '#D0A0E3' },
        { variable: 'primary-dark', value: '#a88ef9' },
    ],
    yellow: [
        { variable: 'primary', value: '#ECA807' },
        { variable: 'primary-light', value: '#FFD267' },
        { variable: 'primary-dark', value: '#F6C03F' },
    ],
    green: [
        { variable: 'primary', value: '#28BDB0' },
        { variable: 'primary-light', value: '#45E8DA' },
        { variable: 'primary-dark', value: '#34CEC0' },
    ],
    black: [
        { variable: 'primary', value: '#dbd4c8' },
        { variable: 'primary-light', value: '#918976' },
        { variable: 'primary-dark', value: 'rgb(16, 19, 23)' },
    ],
};

export const APPEARANCE_LIGHT = 0;
export const APPEARANCE_DARK = 1;
export const APPEARANCE_SYSTEM = 2;

export default merge(Item(), {
    state() {
        return {
            id: null,
            external_id: null,
            email: null,
            name: null,
            first_name: null,
            last_name: null,
            photo_url: null,
            theme_preference: null,
            dark_theme_preference: null,
            quiz_theme_adjustment: null,
            quiz_original_theme_preference: null,
            roles: null,
            enrollments: null,
            permissions: null,
            updated_at: null,
            created_at: null,
            user_groups: null,
            quiz_active_id: null,
            quiz_checkin_id: null,
            quiz_id: null,
            quiz_activity: null,
            admin_or_instruction_admin: null,
            group_instructor: null,
            course_instructor: null,
            identity_verification_active_id: null,
            identity_verification_attempt_active_id: null,

            order: null,
            active_theme: null,
            active_dark_theme: null,
            statuses: null,
        };
    },

    getters: {
        /**
         * Provide the payload to get from.
         * @param state
         * @param getters
         * @returns {function(*): boolean}
         */
        getWithPayload(state, getters) {
            return (getter, payload = {}) => getters[getter](payload);
        },

        /**
         * @param state
         * @returns {function(*): *}
         */
        hasRole: state => search => (state.roles ? Boolean(state.roles.find(role => role.name === search)) : false),

        /**
         * @param state
         * @returns {*}
         */
        id: state => state.id,

        /**
         * @param state
         * @returns {boolean}
         */
        hasActiveQuiz: state => !!state.quiz_active_id,

        /**
         * @param state
         * @returns {boolean}
         */
        hasActiveIdentityAttempt: (state, getters, rootState, rootGetters) => {
            return (
                !!state.identity_verification_active_id &&
                rootGetters['InstitutionSettings/get']('identity_verification_enabled')
            );
        },

        /**
         * @param state
         * @param getters
         * @returns {boolean|*}
         */
        hasActiveAttempt: (state, getters) => getters.hasActiveQuiz || getters.hasActiveIdentityAttempt,

        /**
         * @param state
         * @param getters
         * @returns {function(): *}
         */
        preferredTheme: (state, getters) => () =>
            state.theme_preference ? state.theme_preference : getters.defaultTheme,

        /**
         * @param state
         * @param getters
         * @returns {function(): *}
         */
        preferredDarkTheme: (state, getters) => () => {
            const rawResult =
                state.dark_theme_preference !== null ? state.dark_theme_preference : getters.defaultDarkTheme;
            return Number(rawResult);
        },

        /**
         * @param state
         * @param getters
         * @returns {function(): *}
         */
        activeTheme: (state, getters) => () => (state.active_theme ? state.active_theme : getters.defaultTheme),

        /**
         * @param state
         * @param getters
         * @returns {function(): *}
         */
        activeDarkTheme: (state, getters) => () => {
            const rawResult = state.active_dark_theme !== null ? state.active_dark_theme : getters.defaultDarkTheme;
            return Number(rawResult);
        },

        /**
         * @returns {*}
         */
        name: state => state.name,

        /**
         * @returns {*}
         */
        availableThemes: () => () => THEMES,

        /**
         * @returns {*}
         */
        availableDarkThemes: () => () => DARKTHEMES,

        /**
         * @returns {*}
         */
        defaultTheme: () => 'blue',

        /**
         * @returns {*}
         */
        defaultDarkTheme: () => APPEARANCE_LIGHT,

        /**
         * @param state
         * @param getters
         * @returns {*}
         */
        preferredThemePrimaryColor: (state, getters) => () =>
            getters.activeDarkTheme() === 0
                ? THEMES[getters.preferredTheme()][0].value
                : DARKTHEMES[getters.preferredTheme()][0].value,

        systemThemePrimaryColorDark: (state, getters) => () => DARKTHEMES[getters.preferredTheme()][0].value,

        systemThemePrimaryColorLight: (state, getters) => () => THEMES[getters.preferredTheme()][0].value,

        /**
         * @param state
         * @param getters
         * @returns {boolean}
         */
        hasAdminRights: (state, getters) => {
            const roles = ['Owner', 'Admin', 'Instruction Admin'];

            return roles.some(role => getters.hasRole(role));
        },
    },

    actions: {
        /**
         * @param context
         * @param theme
         */
        async setPreferredTheme(context, theme) {
            context.commit('SET', {
                field: 'theme_preference',
                value: theme,
            });

            await context.dispatch('applyPreferredTheme');

            const { data } = await axios.put('/api/theme-preference', { theme });

            return data.data.theme_preference;
        },

        darkModeThemeHelper(context, appearance) {
            const selectApp = document.getElementsByTagName('html')[0];

            if (appearance) {
                if (context.getters.preferredTheme() === 'purple' || context.getters.preferredTheme() === 'blue') {
                    return document.documentElement.style.setProperty(
                        '--primary',
                        DARKTHEMES[context.getters.preferredTheme()][2].value
                    );
                }

                if (context.getters.preferredTheme() === 'black') {
                    document.documentElement.style.setProperty('--primary', '#dbd4c8');
                    document.documentElement.style.setProperty('--primary-dark', 'rgb(16, 19, 23)');
                    document.documentElement.style.setProperty('--primary--color-invert', '#242B37');

                    return selectApp.classList.add('invert-black');
                }
            }

            document.documentElement.style.setProperty('--primary', THEMES[context.getters.preferredTheme()][0].value);

            if (context.getters.preferredTheme() === 'black' && !appearance) {
                document.documentElement.style.setProperty('--primary-dark', 'rgb(74, 84, 104)');
                document.documentElement.style.setProperty('--primary--color-invert', 'rgb(255, 255, 255)');
            }

            return selectApp.classList.remove('invert-black');
        },

        setSassVariables(context, appearance) {
            const black = 'hsl(0, 0%, 4%)';
            const blackbis = 'hsl(0, 0%, 7%)';
            const blackter = 'hsl(0, 0%, 14%)';

            const greydarker = 'hsl(0, 0%, 21%)';
            const greydark = 'hsl(0, 0%, 29%)';
            const grey = 'hsl(0, 0%, 48%)';
            const greylight = 'hsl(0, 0%, 71%)';
            const _greylighter = 'hsl(0, 0%, 86%)';

            const whiteter = '#f0f7fa';
            const whitebis = 'hsl(0, 0%, 98%)';
            const white = 'hsl(0, 0%, 100%)';

            const sysAppearace = appearance === 2;

            if (appearance === 0 || (sysAppearace && window.matchMedia('(prefers-color-scheme: light)').matches)) {
                document.documentElement.style.setProperty('--scheme-main', white);
                document.documentElement.style.setProperty('--scheme-invert', black);
                document.documentElement.style.setProperty('--scheme-main-bis', whiteter);
                document.documentElement.style.setProperty('--scheme-main-ter', whiteter);
                document.documentElement.style.setProperty('--scheme-invert-bis', blackter);
                document.documentElement.style.setProperty('--scheme-invert-bis-dark', 'hsl(0, 0%, 100%)');
                document.documentElement.style.setProperty('--scheme-invert-ter', blackter);
                document.documentElement.style.setProperty('--text', greydark);
                document.documentElement.style.setProperty('--text-light', grey);
                document.documentElement.style.setProperty('--text-strong', greydarker);
                document.documentElement.style.setProperty('--text-subtle', greylight);
                document.documentElement.style.setProperty('--border', '#e5eff5');
                document.documentElement.style.setProperty('--navbar-item-color', black);
                document.documentElement.style.setProperty('--navbar-item-hover-color', black);
                document.documentElement.style.setProperty('--navbar-dropdown-background-color', whiteter);
                document.documentElement.style.setProperty('--link-hover', greydarker);
                document.documentElement.style.setProperty('--link-hover-border', greylight);
                document.documentElement.style.setProperty('--link-focus', greydarker);
                document.documentElement.style.setProperty('--link-active', greydarker);
                document.documentElement.style.setProperty('--link-active-border', greydark);
                document.documentElement.style.setProperty('--card-background-color', white);
                document.documentElement.style.setProperty(
                    '--modal-background-background-color',
                    `${'rgba(36,43,54,.86)'}`
                );
                document.documentElement.style.setProperty(
                    '--card-shadow-base',
                    `0 0.5em 1em -0.125em ${'rgba(229, 239, 245, 0.8)'}`
                );

                document.documentElement.style.setProperty(
                    '--card-shadow-outline',
                    `0 0.5em 1em -0.125em ${'rgba(229, 239, 245, 0.8)'}`
                );

                document.documentElement.style.setProperty('--sidebar-background', white);
                return document.documentElement.style.setProperty('--background', whiteter);
            }
            document.documentElement.style.setProperty('--scheme-main', '#242b36');
            document.documentElement.style.setProperty('--scheme-invert', white);
            document.documentElement.style.setProperty('--scheme-main-bis', blackbis);
            document.documentElement.style.setProperty('--scheme-main-ter', blackter);
            document.documentElement.style.setProperty('--scheme-invert-bis', whitebis);
            document.documentElement.style.setProperty('--scheme-invert-bis-dark', 'hsl(0, 0%, 100%)');
            document.documentElement.style.setProperty('--scheme-invert-ter', whiteter);
            document.documentElement.style.setProperty('--text', white);
            document.documentElement.style.setProperty('--text-strong', white);
            document.documentElement.style.setProperty('--text-subtle', white);
            document.documentElement.style.setProperty('--border', greydark);
            document.documentElement.style.setProperty('--navbar-item-color', white);
            document.documentElement.style.setProperty('--navbar-item-hover-color', white);
            document.documentElement.style.setProperty('--navbar-dropdown-background-color', blackter);
            document.documentElement.style.setProperty('--link-hover', white);
            document.documentElement.style.setProperty('--link-hover-border', white);
            document.documentElement.style.setProperty('--link-focus', white);
            document.documentElement.style.setProperty('--link-active', white);
            document.documentElement.style.setProperty('--link-active-border', '#242b36');
            document.documentElement.style.setProperty('--card-background-color', '#242b36');
            document.documentElement.style.setProperty(
                '--modal-background-background-color',
                ` ${'rgba(36,43,54,.56)'}`
            );
            document.documentElement.style.setProperty(
                '--card-shadow-base',
                `0 0.5em 1em -0.125em ${'rgba(74, 74, 74, 0.2)'}`
            );
            document.documentElement.style.setProperty(
                '--card-shadow-outline',
                `0 0.5em 1em -0.125em ${'rgba(74, 74, 74, 0.2)'}`
            );

            document.documentElement.style.setProperty('--sidebar-background', black);
            return document.documentElement.style.setProperty('--background', blackter);
        },

        async setDarkPreferredTheme(context, appearance) {
            this.loading = true;

            const darkThemeLinkEl = document.getElementsByClassName('alt-style-element');
            const cloneDarkThemeLinkEl = document.getElementsByClassName('clone-alt-style-element');
            const selectApp = document.getElementsByTagName('html')[0];
            const checkDarkMode =
                appearance === APPEARANCE_LIGHT ||
                (appearance === APPEARANCE_SYSTEM &&
                    window.matchMedia &&
                    window.matchMedia('(prefers-color-scheme: light)').matches);

            const localStorageDarkTheme = Number(localStorage.getItem('darkThemePreference'));

            const currentTheme = Number.isNaN(localStorageDarkTheme)
                ? Number(context.getters.activeDarkTheme())
                : localStorageDarkTheme;

            const sysAppearace = appearance === APPEARANCE_SYSTEM;
            // light mode
            if (checkDarkMode || (sysAppearace && window.matchMedia('(prefers-color-scheme: light)').matches)) {
                context.dispatch('setSassVariables', appearance);

                selectApp.classList.remove('dark-mode');

                if (darkThemeLinkEl[0]) {
                    darkThemeLinkEl[0].parentNode.removeChild(darkThemeLinkEl[0]);
                }

                if (cloneDarkThemeLinkEl[0]) {
                    cloneDarkThemeLinkEl[0].parentNode.removeChild(cloneDarkThemeLinkEl[0]);
                }

                if (this.darkStyleLink) {
                    document.getElementsByClassName('stylesLink')[0].disabled = true;
                }

                context.dispatch('darkModeThemeHelper', false);
            }

            // dark mode
            if (!checkDarkMode) {
                context.dispatch('setSassVariables', appearance);

                selectApp.classList.add('dark-mode');

                if (this.darkStyleLink) {
                    document.getElementsByClassName('stylesLink')[0].disabled = false;
                }

                context.dispatch('darkModeThemeHelper', true);
            }

            if (context.getters.id) {
                await context.commit('SET', {
                    field: 'dark_theme_preference',
                    value: appearance,
                });

                context.commit('SET', {
                    field: 'active_dark_theme',
                    value: appearance,
                });

                if (currentTheme !== appearance && !context.getters.hasActiveQuiz) {
                    const { data } = await axios.put('/api/theme-preference', { appearance });

                    localStorage.setItem('darkThemePreference', appearance);

                    return data.data.dark_theme_preference;
                }

                return appearance;
            }
        },

        /**
         * @param context
         * @returns {Promise<void>}
         */
        async applyPreferredTheme(context) {
            // Check if quiz has ended and restore original theme if needed
            if (!context.getters.hasActiveQuiz && context.state.quiz_original_theme_preference !== null) {
                await context.dispatch('restoreOriginalThemeAfterQuiz');
                return; // restoreOriginalThemeAfterQuiz will call applyPreferredTheme again
            }

            const _preferredTheme = context.getters.preferredTheme() || context.getters.defaultTheme;

            // watches if dark apperance preference is OS preference
            const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');

            const adjustedTheme = await context.dispatch('resetThemeIfActiveQuiz', {
                systemPrefersDark: mediaQuery.matches,
            });

            const handler = function (e) {
                if (context.getters.preferredDarkTheme() === APPEARANCE_SYSTEM) {
                    context.dispatch('setDarkPreferredTheme', context.getters.preferredDarkTheme());
                }
            };

            if (mediaQuery?.addEventListener) {
                mediaQuery.addEventListener('change', _e => {
                    if (context.getters.preferredDarkTheme() === APPEARANCE_SYSTEM) {
                        context.dispatch('setDarkPreferredTheme', context.getters.preferredDarkTheme());
                    }
                });
            } else {
                mediaQuery.addEventListener(handler);
            }

            await context.dispatch('setDarkPreferredTheme', adjustedTheme);
        },

        async resetThemeIfActiveQuiz(context, { systemPrefersDark }) {
            const themeInLocalStorage = Number(localStorage.getItem('darkThemePreference'));
            const themeFromDatabase = Number(context.getters.preferredDarkTheme());

            // During login/hydration, always prioritize database over localStorage
            // This ensures the user's saved preference in DB takes precedence
            const preferredDarkTheme = themeFromDatabase;

            // Sync localStorage if it's different or missing
            if (Number.isNaN(themeInLocalStorage) || themeInLocalStorage !== themeFromDatabase) {
                localStorage.setItem('darkThemePreference', String(themeFromDatabase));
            }

            if (!context.getters.hasActiveQuiz) {
                return preferredDarkTheme;
            }

            // Store original preference before quiz theme adjustment (only if not already stored)
            if (context.state.quiz_original_theme_preference === null) {
                context.commit('SET', { field: 'quiz_original_theme_preference', value: preferredDarkTheme });
            }

            let quizTheme;

            if (preferredDarkTheme === APPEARANCE_SYSTEM) {
                quizTheme = systemPrefersDark ? APPEARANCE_DARK : APPEARANCE_LIGHT;
            } else {
                quizTheme = preferredDarkTheme;
            }

            context.commit('SET', { field: 'dark_theme_preference', value: quizTheme });
            context.commit('SET', { field: 'quiz_theme_adjustment', value: quizTheme });

            await User.update({
                where: context.getters.id,
                data: {
                    dark_theme_preference: quizTheme,
                    quiz_theme_adjustment: quizTheme,
                },
            });

            return quizTheme;
        },

        /**
         * Restore original theme preference when quiz ends
         * @param context
         * @returns {Promise<void>}
         */
        async restoreOriginalThemeAfterQuiz(context) {
            if (context.state.quiz_original_theme_preference !== null) {
                const originalPreference = context.state.quiz_original_theme_preference;

                context.commit('SET', { field: 'dark_theme_preference', value: originalPreference });
                context.commit('SET', { field: 'quiz_original_theme_preference', value: null });
                context.commit('SET', { field: 'quiz_theme_adjustment', value: null });

                await User.update({
                    where: context.getters.id,
                    data: {
                        dark_theme_preference: originalPreference,
                        quiz_theme_adjustment: null,
                    },
                });

                await context.dispatch('applyPreferredTheme');
            }
        },

        async hydrate(context, data) {
            return new Promise(resolve => {
                context.commit('HYDRATE', data);
                resolve();
            });
        },
    },

    mutations: {
        /**
         * @param state
         * @param field
         * @param value
         * @constructor
         */
        SET: (state, { field, value }) => {
            state[field] = value;
        },

        HYDRATE(state, data) {
            Object.keys(state).forEach(key => {
                if (data && typeof data === 'object' && key in data) {
                    let value = data[key];

                    if (key === 'dark_theme_preference' && value !== null) {
                        value = Number(value);
                    }

                    state[key] = value;
                }
            });
        },
    },
});
